import { useMemo } from "react";
import {
  POP_BILLING_BASIS_LABELS,
  POP_DESTINATION_MAX,
  POP_MACROS,
  findUnknownMacros,
  providerAcceptsPop,
  renderPopDestination,
} from "@adlywise/shared";
import Card from "@/components/common/Card";
import { popFormErrors } from "./campaignFormUtils";
import SegmentPicker from "./SegmentPicker";

const API_PREFIX = "/api";
const API_BASE = import.meta.env?.VITE_API_URL ?? API_PREFIX;

// The tracker postback lives at the host root (like /vast and /rtb), never
// under /api. One URL per status, with the status FIXED: the server accepts
// only landing / registration / fdp, so a tracker's own status names must
// never be passed through. Keitaro substitutes {sub_id_1} (our pop id) and
// {payout}; payouts are recorded in USD unless the URL adds &cur=XXX.
export const POP_POSTBACK_STATUSES = ["landing", "registration", "fdp"];
export const popPostbackUrl = (token, status, origin) => {
  let base = API_BASE.replace(/\/+$/, "");
  if (base.endsWith(API_PREFIX)) base = base.slice(0, -API_PREFIX.length);
  const tail = status === "landing" ? "" : "&payout={payout}";
  const path = `${base}/vast/pop/pb/${token}?cid={sub_id_1}&status=${status}${tail}`;
  try {
    return new URL(path, origin || window.location.origin).href.replace(
      /%7B([a-z0-9_]+)%7D/gi,
      "{$1}",
    );
  } catch {
    return path;
  }
};

// Sample values for the live destination preview.
const PREVIEW = {
  sub_id_1: "0192f0c4-7d1e-7a00-9b2c-1f2e3d4c5b6a",
  zone: "123456",
  provider: "provider-uuid",
  campaign: "campaign-uuid",
  cost: "0.002000",
  country: "UA",
  os: "android",
  device: 4,
};

const isPopPublisher = (p) => Array.isArray(p.types) && providerAcceptsPop(p);

export default function PopTargetStep({
  form,
  segments = [],
  providers,
  providersLoading,
  onPopChange,
  onFieldChange,
}) {
  const pop = form.pop;
  const publishers = useMemo(
    () => (Array.isArray(providers) ? providers : []).filter(isPopPublisher),
    [providers],
  );
  // Selected publishers that no longer accept popunder: shown so they can be
  // removed (the API refuses to save a pop campaign that targets them).
  const stranded = useMemo(() => {
    const ok = new Set(publishers.map((p) => p.uuid));
    const byUuid = new Map(
      (Array.isArray(providers) ? providers : []).map((p) => [p.uuid, p]),
    );
    return pop.selectedPublishers
      .filter((u) => !ok.has(u))
      .map((u) => ({ uuid: u, name: byUuid.get(u)?.name ?? u }));
  }, [publishers, providers, pop.selectedPublishers]);
  const unknown = findUnknownMacros(pop.destination);
  const errors = popFormErrors(pop, form.cpm);
  const togglePublisher = (uuid) =>
    onPopChange({
      selectedPublishers: pop.selectedPublishers.includes(uuid)
        ? pop.selectedPublishers.filter((u) => u !== uuid)
        : [...pop.selectedPublishers, uuid],
    });

  const field = (key, label, props = {}) => (
    <div className="form-group-modal">
      <label htmlFor={`pop-${key}`}>{label}</label>
      <input
        id={`pop-${key}`}
        value={pop[key]}
        onChange={(e) => onPopChange({ [key]: e.target.value })}
        {...props}
      />
    </div>
  );

  return (
    <Card className="builder-form flex flex-col gap-5">
      <div className="form-group-modal">
        <label htmlFor="pop-destination">Destination (tracker URL)</label>
        <textarea
          id="pop-destination"
          rows={3}
          value={pop.destination}
          onChange={(e) => onPopChange({ destination: e.target.value })}
          placeholder="https://tracker.example/abc?sub_id_1={sub_id_1}"
        />
        <p className="form-help-text">
          {pop.destination.length} / {POP_DESTINATION_MAX} characters. Macros:{" "}
          {POP_MACROS.map((m) => `{${m}}`).join(" ")}
        </p>
        {unknown.length > 0 && (
          <p className="form-help-text text-(--danger)">
            Unknown macro: {unknown.map((m) => `{${m}}`).join(", ")}
          </p>
        )}
        {pop.destination && unknown.length === 0 && (
          <p className="form-help-text break-all" data-testid="pop-preview">
            Preview: {renderPopDestination(pop.destination, PREVIEW)}
          </p>
        )}
      </div>

      <div className="form-group-modal">
        <label>Publishers (popunder-enabled)</label>
        {providersLoading ? (
          <p className="form-help-text">Loading…</p>
        ) : publishers.length === 0 ? (
          <p className="form-help-text">
            No publisher has popunder configured yet (Providers → Popunder
            buying).
          </p>
        ) : (
          <div className="flex flex-col gap-1">
            {publishers.map((p) => (
              <label key={p.uuid} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={pop.selectedPublishers.includes(p.uuid)}
                  onChange={() => togglePublisher(p.uuid)}
                />
                <span>
                  {p.name}
                  {p.settings?.pop?.enabled ? "" : " (not bidding yet)"}
                </span>
              </label>
            ))}
          </div>
        )}
        {stranded.map((p) => (
          <label
            key={p.uuid}
            className="flex items-center gap-2 text-(--danger)"
          >
            <input
              type="checkbox"
              checked
              onChange={() => togglePublisher(p.uuid)}
            />
            <span>{p.name} — no longer accepts popunder, untick to remove</span>
          </label>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="form-group-modal">
          <label htmlFor="pop-cpm">Sell CPM (USD)</label>
          <input
            id="pop-cpm"
            type="number"
            min="0"
            step="0.01"
            value={form.cpm}
            onChange={(e) => onFieldChange("cpm", e.target.value)}
          />
        </div>
        {field("marginPct", "Margin % (bid = CPM less margin)", {
          type: "number",
          min: 1,
          max: 90,
        })}
        <div className="form-group-modal">
          <label htmlFor="pop-budget">
            Budget cap (USD, advertiser charge on the billing basis)
          </label>
          <input
            id="pop-budget"
            type="number"
            min="0"
            step="0.01"
            value={form.budgetCap}
            onChange={(e) => onFieldChange("budgetCap", e.target.value)}
          />
        </div>
        <div className="form-group-modal">
          <label htmlFor="pop-daily-budget">
            Daily budget cap (USD, advertiser charge)
          </label>
          <input
            id="pop-daily-budget"
            type="number"
            min="0"
            step="0.01"
            value={form.dailyBudgetCap ?? ""}
            onChange={(e) => onFieldChange("dailyBudgetCap", e.target.value)}
          />
        </div>
        {field("capCount", "Visits per user", {
          type: "number",
          min: 1,
          max: 10,
        })}
        {field("capWindowHours", "…per hours", {
          type: "number",
          min: 1,
          max: 168,
        })}
        <div className="form-group-modal">
          <label htmlFor="pop-impressions">Billable pops cap</label>
          <input
            id="pop-impressions"
            type="number"
            min="0"
            value={form.impressionsCap}
            onChange={(e) => onFieldChange("impressionsCap", e.target.value)}
          />
        </div>
      </div>
      <div className="form-group-modal">
        <label htmlFor="pop-billing-basis">Billing based on</label>
        <select
          id="pop-billing-basis"
          value={pop.billingBasis}
          onChange={(e) =>
            onPopChange({ billingBasis: Number(e.target.value) })
          }
        >
          {Object.entries(POP_BILLING_BASIS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <p className="form-help-text">
          Budget, revenue and what the advertiser is charged count this event;
          SSP cost is unaffected.
        </p>
      </div>

      {pop.legacyGeo && (
        <div className="rounded-(--radius-md) border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          <strong>Not bidding: legacy country targeting.</strong> This campaign
          still targets{" "}
          {[
            pop.legacyGeo.include?.length
              ? `countries ${pop.legacyGeo.include.join(", ")}`
              : "any country",
            pop.legacyGeo.exclude?.length
              ? `except ${pop.legacyGeo.exclude.join(", ")}`
              : null,
            pop.legacyGeo.excludeRegions?.length
              ? `without regions ${pop.legacyGeo.excludeRegions.join(", ")}`
              : null,
          ]
            .filter(Boolean)
            .join(" ")}
          . Targeting now lives in audiences; an admin converts it to an
          audience segment before it bids again.
        </div>
      )}
      <div className="form-group-modal">
        <label>Audiences</label>
        <SegmentPicker
          segments={segments}
          value={form.selectedSegments}
          onChange={(next) => onFieldChange("selectedSegments", next)}
        />
        {pop.legacyGeo ? null : form.selectedSegments.length === 0 ? (
          <p className="text-xs font-medium text-amber-600">
            No audience: this campaign bids on every country and region.
          </p>
        ) : (
          <p className="text-xs text-gray-500">
            Bids when the visitor matches any of these audiences. Country,
            region and the other request fields work on pop traffic; device tags
            and other enriched data do not. To leave regions out, list the
            allowed regions: an excluded-region rule lets requests with no
            region through.
          </p>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="form-group-modal">
          <label htmlFor="pop-inc-zones">
            Only these zones (one per line; empty ⇒ any)
          </label>
          <textarea
            id="pop-inc-zones"
            rows={3}
            value={pop.includedZones}
            onChange={(e) => onPopChange({ includedZones: e.target.value })}
          />
        </div>
        <div className="form-group-modal">
          <label htmlFor="pop-inc-domains">
            Only these site domains (subdomains included; empty ⇒ any)
          </label>
          <textarea
            id="pop-inc-domains"
            rows={3}
            value={pop.includedSiteDomains}
            onChange={(e) =>
              onPopChange({ includedSiteDomains: e.target.value })
            }
          />
        </div>
        <div className="form-group-modal">
          <label htmlFor="pop-zones">Never these zones</label>
          <textarea
            id="pop-zones"
            rows={3}
            value={pop.excludedZones}
            onChange={(e) => onPopChange({ excludedZones: e.target.value })}
          />
        </div>
        <div className="form-group-modal">
          <label htmlFor="pop-domains">Never these site domains</label>
          <textarea
            id="pop-domains"
            rows={3}
            value={pop.excludedSiteDomains}
            onChange={(e) =>
              onPopChange({ excludedSiteDomains: e.target.value })
            }
          />
        </div>
      </div>
      <p className="text-xs text-gray-500">
        A whitelist is strict: when it has entries, the campaign bids only on
        traffic from them, and never on requests that carry no zone or site.
      </p>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="form-group-modal">
          <label htmlFor="pop-confirm">Billable visit</label>
          <select
            id="pop-confirm"
            value={pop.confirmMode}
            onChange={(e) => onPopChange({ confirmMode: e.target.value })}
          >
            <option value="tracker">Tracker landing postback</option>
            <option value="fingerprint">Fingerprint matches the bid</option>
          </select>
        </div>
        <div className="form-group-modal">
          <label htmlFor="pop-cost-macro">{"{cost}"} macro</label>
          <select
            id="pop-cost-macro"
            value={pop.costMacro}
            onChange={(e) => onPopChange({ costMacro: e.target.value })}
          >
            <option value="sell">What the advertiser pays</option>
            <option value="buy">What we pay</option>
          </select>
        </div>
        <div className="form-group-modal">
          <label htmlFor="pop-cpa">Optimise on</label>
          <select
            id="pop-cpa"
            value={pop.cpaTarget}
            onChange={(e) => onPopChange({ cpaTarget: e.target.value })}
          >
            <option value="fdp">First deposit</option>
            <option value="registration">Registration</option>
          </select>
        </div>
      </div>
      {field("adomain", "Advertiser domain (bid adomain, optional)")}

      {pop.postbackToken ? (
        <div className="form-group-modal">
          <label>Tracker postback URLs</label>
          {POP_POSTBACK_STATUSES.filter(
            (st) => st !== "landing" || pop.confirmMode === "tracker",
          ).map((st) => (
            <code
              key={st}
              className="block break-all text-xs"
              data-testid={`pop-postback-${st}`}
            >
              {popPostbackUrl(pop.postbackToken, st)}
            </code>
          ))}
          <p className="form-help-text">
            One postback per event, each with its status fixed. Payouts are
            recorded in USD unless the URL adds &cur=XXX.
          </p>
        </div>
      ) : (
        <p className="form-help-text">
          The tracker postback URL appears here after the campaign is saved.
        </p>
      )}

      {errors.length > 0 && (
        <ul className="form-help-text text-(--danger)" data-testid="pop-errors">
          {errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
    </Card>
  );
}
